import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import { FaUserEdit, FaSave, FaUserCircle, FaCompass, FaFingerprint, FaCalendarAlt } from 'react-icons/fa';
import BackgroundEffect from '../components/BackgroundEffect';

const AdminProfile = () => {
    const [adminData, setAdminData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ user_name: '', mobile_no: '' });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const storedAdmin = localStorage.getItem('adminUser');
        if (storedAdmin) {
            const data = JSON.parse(storedAdmin);
            setAdminData(data);
            setFormData({ user_name: data.user_name, mobile_no: data.mobile_no });
        }
    }, []);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await axios.put(`${BASE_URL}/users/updateUser.php`, {
                user_id: adminData.user_id,
                user_name: formData.user_name,
                mobile_no: formData.mobile_no
            });

            if (response.data.status === 200) {
                const updatedAdmin = { ...adminData, ...formData };
                setAdminData(updatedAdmin);
                localStorage.setItem('adminUser', JSON.stringify(updatedAdmin));
                setMessage({ type: 'success', text: 'Profile updated successfully!' });
                setIsEditing(false);
            } else {
                setMessage({ type: 'error', text: response.data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Update failed. System link error.' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!adminData) return <div className="text-center py-5">AUTHENTICATING...</div>;

    return (
        <div className="admin-profile-page">
            <BackgroundEffect />

            <div className="container py-3 mt-3">
                <div className="mb-4">
                    <span className="section-index">ADMINISTRATIVE IDENTITY</span>
                    <h2 className="display-6 text-white text-uppercase fw-lighter" style={{ letterSpacing: '4px' }}>
                        Profile <span style={{ color: 'var(--secondary-color)' }}>Nexus</span>
                    </h2>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-7">
                        <div className="admin-profile-card">
                            <div className="card-blueprint-corner top-left"></div>
                            <div className="card-blueprint-corner top-right"></div>
                            <div className="card-blueprint-corner bottom-left"></div>
                            <div className="card-blueprint-corner bottom-right"></div>

                            <div className="text-center mb-4">
                                <div className="profile-hero-wrap mb-3">
                                    <div className="avatar-glow"></div>
                                    <FaUserCircle className="profile-icon-main" />
                                    <div className="admin-badge-gold">AUTHORIZED OPERATOR</div>
                                </div>
                                <h3 className="text-white mt-3 fw-lighter text-uppercase" style={{ letterSpacing: '2px' }}>
                                    {adminData.user_name}
                                </h3>
                            </div>

                            {isEditing ? (
                                <form onSubmit={handleUpdate} className="profile-form-compact">
                                    <div className="mb-4">
                                        <div className="input-group-arch">
                                            <label className="arch-label">FULL OPERATIONAL NAME</label>
                                            <input
                                                type="text"
                                                className="arch-input-gold"
                                                value={formData.user_name}
                                                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <div className="input-group-arch">
                                            <label className="arch-label">SECURE CONTACT LINK</label>
                                            <input
                                                type="tel"
                                                className="arch-input-gold"
                                                value={formData.mobile_no}
                                                onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {message.text && (
                                        <div className={`admin-msg-premium mb-4 ${message.type}`}>
                                            {message.text}
                                        </div>
                                    )}

                                    <div className="d-flex gap-3">
                                        <button type="submit" className="btn-admin-submit flex-grow-1" disabled={isLoading}>
                                            {isLoading ? 'ENCRYPTING...' : <><FaSave className="me-2" /> COMMIT CHANGES</>}
                                        </button>
                                        <button type="button" className="btn-admin-outline" onClick={() => setIsEditing(false)}>
                                            ABORT
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="profile-blueprint-grid">
                                    <div className="blueprint-row">
                                        <div className="bp-label">
                                            <FaFingerprint className="me-3" color="var(--secondary-color)" />
                                            SECURE MOBILE PORT
                                        </div>
                                        <div className="bp-value text-white">{adminData.mobile_no}</div>
                                    </div>

                                    <div className="blueprint-row">
                                        <div className="bp-label">
                                            <FaCalendarAlt className="me-3" color="var(--secondary-color)" />
                                            SYSTEM LOG DATE
                                        </div>
                                        <div className="bp-value text-white-50">{new Date().toLocaleDateString()}</div>
                                    </div>

                                    <div className="blueprint-row border-0">
                                        <div className="bp-label">
                                            <FaCompass className="me-3" color="var(--secondary-color)" />
                                            OPERATIONAL SECTOR
                                        </div>
                                        <div className="bp-value text-white-50">HEADQUARTERS / ADMIN</div>
                                    </div>

                                    <button className="btn-admin-overhaul w-100 mt-5" onClick={() => setIsEditing(true)}>
                                        <FaUserEdit className="me-2" /> MODIFY PROFILE STRUCTURE
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .admin-profile-page {
                    min-height: 100vh;
                    position: relative;
                    z-index: 1;
                }

                .admin-profile-card {
                    background: rgba(10, 10, 10, 0.4);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    padding: 35px 50px;
                    position: relative;
                    border-radius: 4px;
                }

                .card-blueprint-corner {
                    position: absolute;
                    width: 20px;
                    height: 20px;
                    border-color: var(--secondary-color);
                    border-style: solid;
                    opacity: 0.3;
                }
                .top-left { top: 20px; left: 20px; border-width: 1px 0 0 1px; }
                .top-right { top: 20px; right: 20px; border-width: 1px 1px 0 0; }
                .bottom-left { bottom: 20px; left: 20px; border-width: 0 0 1px 1px; }
                .bottom-right { bottom: 20px; right: 20px; border-width: 0 1px 1px 0; }

                .profile-hero-wrap {
                    position: relative;
                    display: inline-block;
                }

                .avatar-glow {
                    position: absolute;
                    inset: -15px;
                    background: radial-gradient(circle, rgba(197, 160, 89, 0.2) 0%, transparent 70%);
                    z-index: -1;
                    animation: pulseGlow 4s infinite alternate;
                }

                @keyframes pulseGlow {
                    from { transform: scale(0.9); opacity: 0.5; }
                    to { transform: scale(1.1); opacity: 1; }
                }

                .profile-icon-main {
                    font-size: 70px;
                    color: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(197, 160, 89, 0.1);
                    padding: 12px;
                    border-radius: 50%;
                }

                .admin-badge-gold {
                    position: absolute;
                    bottom: -5px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: var(--secondary-color);
                    color: #000;
                    font-size: 0.6rem;
                    font-weight: 800;
                    letter-spacing: 2px;
                    padding: 4px 15px;
                    border-radius: 2px;
                    white-space: nowrap;
                }

                .blueprint-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 18px 0;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
                }

                .bp-label {
                    color: rgba(255, 255, 255, 0.4);
                    font-size: 0.75rem;
                    letter-spacing: 2px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                }

                .bp-value {
                    font-size: 1rem;
                    letter-spacing: 1px;
                }

                /* Form Enhancements */
                .input-group-arch {
                    border-left: 2px solid var(--secondary-color);
                    padding-left: 20px;
                }

                .arch-label {
                    display: block;
                    color: var(--secondary-color);
                    font-size: 0.6rem;
                    letter-spacing: 3px;
                    margin-bottom: 10px;
                }

                .arch-input-gold {
                    background: transparent;
                    border: none;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                    color: white;
                    width: 100%;
                    padding: 10px 0;
                    font-size: 1.2rem;
                    letter-spacing: 1px;
                    outline: none;
                    transition: all 0.3s ease;
                }

                .arch-input-gold:focus {
                    border-color: var(--secondary-color);
                    background: linear-gradient(90deg, rgba(197, 160, 89, 0.05), transparent);
                }

                .btn-admin-overhaul {
                    background: transparent;
                    border: 1px solid var(--secondary-color);
                    color: var(--secondary-color);
                    padding: 15px;
                    font-size: 0.75rem;
                    letter-spacing: 2px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }

                .btn-admin-overhaul:hover {
                    background: var(--secondary-color);
                    color: #000;
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(197, 160, 89, 0.2);
                }

                .admin-msg-premium {
                    padding: 12px;
                    font-size: 0.8rem;
                    text-align: center;
                    letter-spacing: 1px;
                    border-radius: 2px;
                }
                .admin-msg-premium.success { background: rgba(40, 167, 69, 0.1); color: #28a745; border: 1px solid rgba(40, 167, 69, 0.2); }
                .admin-msg-premium.error { background: rgba(220, 53, 69, 0.1); color: #dc3545; border: 1px solid rgba(220, 53, 69, 0.2); }

                @media (max-width: 768px) {
                    .admin-profile-card { padding: 30px 20px; }
                    .bp-value { font-size: 0.9rem; }
                    .display-4 { font-size: 2.5rem; }
                }
            `}</style>
        </div>
    );
};

export default AdminProfile;
